"use client";

import React, { useState } from "react";
import { useGlobalData } from "@/components/TinaContext";
import { cn, tinaField } from "@/utils";

import Logo from "@/assets/logo.svg";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useForm } from "react-hook-form";

import {
  FaFacebook as FacebookIcon,
  FaInstagram as InstagramIcon,
  FaLinkedin as LinkedInIcon,
  FaYoutube as YouTubeIcon,
  FaGithub as GithubIcon,
} from "react-icons/fa";
import { FaXTwitter as XIcon } from "react-icons/fa6";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { LinkIcon, Loader2, Check } from "lucide-react";
import { isInternalLink } from "@/components/ui/button";
import { subscribeToNewsletter } from "@/lib/hubspot";
import { usePathname } from "next/navigation";

export const PLATFORMS_ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
  youtube: YouTubeIcon,
  github: GithubIcon,
  website: LinkIcon,
};

export default function Footer() {
  const navItems = useGlobalData();
  const { footer } = navItems;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const pathname = usePathname();

  const form = useForm({
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const result = await subscribeToNewsletter(
        data.email,
        pathname || undefined
      );
      if (result.success) {
        setIsSubscribed(true);
        form.reset();
        // Reset subscribed state after 5 seconds
        setTimeout(() => {
          setIsSubscribed(false);
        }, 3000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="pt-0 lg:pt-12 pb-8">
      <div className="content flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.75fr_repeat(4,1fr)] divide-y lg:divide-y-0 lg:divide-x divide-gray-700 max-lg:[&>div]:py-6 lg:[&>div]:px-6">
          <div className="flex flex-col justify-between min-h-64 gap-4 lg:gap-24 !px-0 col-span-1 sm:col-span-2 lg:col-span-1">
            <div
              data-tina-field={tinaField(footer, "title")}
              className="h2 pr-4"
            >
              {footer?.title}
            </div>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-3 pr-6"
              >
                <p id="footer-newsletter-heading">Subscribe to our newsletter</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-1.5 flex-1 w-full">
                        <FormLabel className="sr-only">Email address</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your email"
                            autoComplete="email"
                            {...field}
                            type="email"
                            required
                            disabled={isSubmitting || isSubscribed}
                            className="w-full field !bg-gray-800/50"
                            aria-required="true"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <Button
                    variant="outline"
                    type="submit"
                    disabled={isSubmitting}
                    className={cn("min-w-[120px]", {
                      "border-secondary-500 text-secondary-500 hover:bg-green-500/10":
                        isSubscribed,
                    })}
                  >
                    {isSubmitting ? (
                      <Loader2 className="animate-spin size-4" />
                    ) : isSubscribed ? (
                      <span className="flex items-center gap-2">
                        <Check className="size-4" />
                        Subscribed
                      </span>
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
          {footer?.primaryNavigationItems?.map((item, itemIndex) => (
            <div
              key={itemIndex}
              className="flex flex-col gap-4 last:max-lg:border-b"
            >
              <div data-tina-field={tinaField(item, "title")} className="h5">
                {item.title}
              </div>
              <div className="flex flex-col gap-3">
                {item?.links?.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    href={link?.url || "#"}
                    // data-tina-field={tinaField(link, "label")}
                    target={isInternalLink(link?.url) ? "_self" : "_blank"}
                    className={cn(
                      "text-[17px] text-gray-400 transition-all duration-300",
                      {
                        "!cursor-not-allowed opacity-50": !link.url,
                        "hover:text-white": link.url,
                      }
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[100px_1fr_200px] gap-8 py-2 md:py-6 lg:border-t border-gray-700 items-center">
          <div className="flex items-center justify-start md:justify-start">
            <Link href="/" aria-label="Binarly home">
              <Logo className="h-9" aria-hidden />
            </Link>
          </div>
          <div className="flex flex-wrap flex-col sm:flex-row items-start justify-center md:justify-start gap-4">
            <div className="text-sm text-gray-500">
              © Copyright {new Date().getFullYear()}
            </div>
            {footer?.secondaryNavigation.map((item, index) => (
              <Link
                key={index}
                // data-tina-field={tinaField(item, "label")}
                className="text-sm text-gray-500 hover:text-white transition-all duration-300 cursor-pointer !font-mono"
                href={item?.url || "#"}
                target={isInternalLink(item?.url) ? "_self" : "_blank"}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center justify-start md:justify-end gap-2">
            {footer?.socials?.map((social, socialIndex) => {
              const Icon =
                PLATFORMS_ICONS[social?.platform?.toLowerCase()] || null;

              if (!Icon) return null;
              return (
                <Link
                  key={socialIndex}
                  className="rounded-full p-2 border border-gray-700 hover:bg-white transition-all duration-300 cursor-pointer group"
                  href={social?.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Binarly on ${social?.platform ?? "social"}`}
                >
                  <Icon
                    className="size-4 text-gray-500 group-hover:text-black transition-all duration-300"
                    aria-hidden
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
